import React from 'react';

import { useQuery } from '@tanstack/react-query';
import moment from 'moment';

import { useParams } from 'react-router-dom';
import Layout from '../../containers/Layout';
import Table from '../../components/Table';
import FormContainer from '../../components/AddOrEditPage/FormContainer';
import Alert from '../../components/Alert';

import formatCurrency from '../../helpers/formatCurrency';
import useBillComSettlementForm from '../../forms/billComSettlements/billComSettlement';
import BillcomSettlementParentService from '../../services/BillcomSettlementParent';
import sumDollarAmounts from '../../helpers/sumDollarAmounts';

const createAlertMessage = (billComSettlementParent) => {
  if (!billComSettlementParent) {
    return '';
  }

  if (billComSettlementParent.type === 'Payable') {
    return `$${formatCurrency(
      Math.abs(billComSettlementParent.amount)
    )} was withdrawn from ${
      billComSettlementParent._bank_account?.name_on_account || ''
    } ${billComSettlementParent._bank_account?.function || ''} account on ${
      billComSettlementParent.process_date
        ? moment(billComSettlementParent.process_date).format('M/D/YY')
        : ''
    }`;
  }

  if (billComSettlementParent.type === 'Receivable') {
    return `$${formatCurrency(
      billComSettlementParent.amount
    )} was deposited in the ${
      billComSettlementParent._bank_account?.name_on_account || ''
    } ${billComSettlementParent._bank_account?.function || ''} account on ${
      billComSettlementParent.process_date
        ? moment(billComSettlementParent.process_date).format('M/D/YY')
        : ''
    }`;
  }

  return '';
};

const paymentDetailsCols = [
  {
    title: 'Date',
    keys: ['date'],
    type: 'date',
  },
  {
    title: 'Property',
    keys: ['_property.address_line_1'],
  },
  {
    title: 'Borrower',
    keys: ['_borrower_primary'],
    type: 'borrower',
  },
  {
    title: 'Servicing Fees',
    keys: ['_payment_line_items'],
    type: 'currency',
    format: (value) => {
      const servicingFeeLineItems = value?.filter(
        (lineItem) => lineItem._line_item?._account.name === 'Servicing Fees'
      );
      return sumDollarAmounts(servicingFeeLineItems, 'amount');
    },
  },
  {
    title: 'PI',
    keys: ['_payment_line_items'],
    type: 'currency',
    format: (value) => {
      const servicingFeeLineItems = value?.filter(
        (lineItem) =>
          lineItem._line_item?._account.name === 'Principal' ||
          lineItem._line_item?._account.name === 'Interest'
      );
      return sumDollarAmounts(servicingFeeLineItems, 'amount');
    },
  },
  {
    title: 'Impounds',
    keys: ['_payment_line_items'],
    type: 'currency',
    format: (value) => {
      const servicingFeeLineItems = value?.filter(
        (lineItem) =>
          lineItem._line_item?._account.name === 'Insurance Impound' ||
          lineItem._line_item?._account.name === 'Property Tax Impound' ||
          lineItem._line_item?._account.name === 'HOA Impound'
      );
      return sumDollarAmounts(servicingFeeLineItems, 'amount');
    },
  },
  {
    title: 'Late fees',
    keys: ['_payment_line_items'],
    type: 'currency',
    format: (value) => {
      const servicingFeeLineItems = value?.filter(
        (lineItem) =>
          lineItem._line_item?._account.name === 'Servicer Penalties' ||
          lineItem._line_item?._account.name === 'Lender Penalties'
      );
      return sumDollarAmounts(servicingFeeLineItems, 'amount');
    },
  },
  {
    title: 'Other',
    keys: ['_payment_line_items'],
    type: 'currency',
    format: (value) => {
      const servicingFeeLineItems = value?.filter(
        (lineItem) =>
          lineItem._line_item?._account.name !== 'Servicer Penalties' &&
          lineItem._line_item?._account.name !== '"Lender Penalties' &&
          lineItem._line_item?._account.name !== 'Insurance Impound' &&
          lineItem._line_item?._account.name !== 'Property Tax Impound' &&
          lineItem._line_item?._account.name !== 'HOA Impound' &&
          lineItem._line_item?._account.name !== 'Principal' &&
          lineItem._line_item?._account.name !== 'Interest' &&
          lineItem._line_item?._account.name !== 'Servicing Fees'
      );
      return sumDollarAmounts(servicingFeeLineItems, 'amount');
    },
  },
];

export default function BillcomSettlementPage() {
  const { id } = useParams();

  const { data: billComSettlementParent } = useQuery({
    queryKey: ['billComSettlement', id],
    queryFn: () =>
      BillcomSettlementParentService.getBillComSettlementParent(id),
    enabled: !!id,
  });

  // console.log(billComSettlementParent);

  const billComSettlementParentWithBankAccount = {
    ...billComSettlementParent,
    bankAccount: `${
      billComSettlementParent?._bank_account?.bank_name || ''
    } - ${
      billComSettlementParent?._bank_account?.account_number?.replace(
        /.(?=.{4})/g,
        '* '
      ) || ''
    }`,
  };

  const billComSettlementForm = useBillComSettlementForm(
    () => {},
    billComSettlementParentWithBankAccount
  );

  const cols = [
    {
      title: 'Date',
      keys: ['date'],
      format: (value) => moment(value).format('M/D/YY'),
    },
    {
      title: 'Amount',
      keys: ['amount'],
      format: (value) => {
        return `$${formatCurrency(value)}`;
      },
    },
    {
      title: 'Name',
      keys: ['name'],
      headerTooltip: () => {
        if (billComSettlementParent?.type === 'Payable') {
          return 'The name of the account where funds for this payable settlement were sent';
        }
        if (billComSettlementParent?.type === 'Receivable') {
          return 'The name of the account where funds were drawn to compose this receivable settlement';
        }

        return null;
      },
    },
    {
      title: 'Transactions',
      keys: ['123'],
      format: (value, row) => {
        if (row._bill_payment) {
          const hasProperty = row._bill_payment?._property?.address_line_1;

          return `$${formatCurrency(row._bill_payment.amount)}${
            hasProperty
              ? ` - ${row._bill_payment._property.address_line_1}`
              : ''
          }`;
        }

        if (row._payment) {
          const hasProperty = row._payment?._property?.address_line_1;
          return `$${formatCurrency(row._payment.amount)}${
            hasProperty ? ` - ${row._payment._property.address_line_1}` : ''
          }`;
        }

        return '';
      },
      link: (row) => {
        if (row._bill_payment) {
          return `/bill-payments/${row._bill_payment.id}`;
        }

        if (row._payment) {
          return `/loans/${row._payment.loan_id}/payments/${row._payment.id}?tab=Payment`;
        }

        return '';
      },
    },
  ];

  const isReceivable = billComSettlementParent?.type === 'Receivable';
  const billComSettlementsPayments = isReceivable
    ? billComSettlementParent?._billcom_settlements.map(
        (settlement) => settlement._payment
      )
    : [];

  return (
    <Layout>
      <div className="flex">
        {/* Content area */}
        <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          <main className="grow">
            <div className="px-4 sm:px-6 lg:px-8 py-8 pb-10 w-full max-w-9xl mx-auto">
              {/* Page header */}
              <div className="sm:flex sm:justify-between sm:items-center mb-8">
                {/* Left: Title */}
                <div className="mb-4 sm:mb-0">
                  <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
                    Bill.com Settlement Details Page
                  </h1>
                </div>
              </div>

              <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
                <div className="mb-10">
                  <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-6">
                    {billComSettlementParent?.description}
                  </h2>
                  <Alert
                    message={createAlertMessage(billComSettlementParent)}
                  />
                  <FormContainer
                    groupSizeOffset={10}
                    isVisible={false}
                    form={billComSettlementForm}
                    noBillComId
                    noSubmit
                  />
                </div>
                <Table
                  cols={cols}
                  rows={billComSettlementParent?._billcom_settlements}
                  title="Settlement Transactions"
                  minHeight={565}
                />
                {isReceivable && (
                  <div className="mt-10">
                    <Table
                      cols={paymentDetailsCols}
                      rows={billComSettlementsPayments}
                      title="Payment Details"
                      colsWithTotals={[
                        'Servicing Fees',
                        'PI',
                        'Impounds',
                        'Late fees',
                        'Other',
                      ]}
                    />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </Layout>
  );
}
